import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  AreaSeries,
  ChartTooltip,
  Line,
  LinearXAxis,
  LinearXAxisTickLabel,
  LinearXAxisTickSeries,
  LinearYAxis,
  LinearYAxisTickLabel,
  LinearYAxisTickSeries,
  schemes,
  TooltipArea,
} from 'reaviz';
import { CustomTooltipContentAttendanceChart } from '../../../../tools/components/CustomTooltipContentAttendanceChart';
import { AttendancePeriodType, UserSeriesData } from '../../hooks/useAttendanceData';
import { AttendanceAPIResponse } from '../../types/attendance.types';
import { UserAttendanceLegend } from './UserAttendanceLegend';

// Export color scheme for use in other components
export const CHART_COLOR_SCHEME = schemes.unifyviz;

interface UserAttendanceChartProps {
  data: UserSeriesData[];
  formatXAxisDate: (value: any) => string;
  apiData?: AttendanceAPIResponse;
  selectedUsers?: number[];
  onUserSelectionChange?: (userId: number) => void;
  selectedPeriod?: AttendancePeriodType;
}

function getMedian(data: number[]): number {
  const sorted = [...data].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

export const UserAttendanceChart: React.FC<UserAttendanceChartProps> = ({
  data,
  formatXAxisDate,
  apiData,
  selectedUsers = [],
  onUserSelectionChange,
  selectedPeriod = 'current_month',
}) => {
  const { t } = useTranslation('dashboard');
  // Determine period type for tooltip formatting
  const getPeriodType = (): 'day' | 'week' | 'month' | 'year' => {
    switch (selectedPeriod) {
      case 'current_month':
        return 'day';
      case 'last_3_months':
        return 'week';
      case 'last_6_months':
        return 'month';
      default:
        return 'day';
    }
  };

  const periodType = getPeriodType();

  const userColors = apiData?.users.map((user, index) => {
    return {
      key: user.id,
      color: CHART_COLOR_SCHEME[index % CHART_COLOR_SCHEME.length],
    };
  });

  // Calculate maximum hours value from data for Y axis configuration
  const maxHoursValue = useMemo(() => {
    if (!data || data.length === 0) return 10; // Default 10 hours

    let maxValue = 0;

    // Iterate through all data series
    data.forEach((series) => {
      if (series.data && Array.isArray(series.data)) {
        // Find maximum value in each series
        series.data.forEach((point) => {
          if (point.value > maxValue) {
            maxValue = point.value;
          }
        });
      }
    });

    // Round up maximum value to nearest integer and add margin
    const roundedMax = Math.ceil(maxValue);
    return Math.max(10, roundedMax + 2); // Minimum 10 hours, or maximum value + 2 hours margin
  }, [data]);

  return (
    <div className="flex h-full">
      {/* Left column with chart (80% width) */}
      <div className="flex-none w-[80%]">
        <AreaChart
          data={data}
          height={250}
          series={
            <AreaSeries
              type="grouped"
              interpolation="linear"
              colorScheme={(data, index) => {
                const seriesColor = userColors?.find((item) => item.key === data?.[0].metadata.userId)?.color;
                return seriesColor || 'red';
              }}
              area={
                <Area
                  style={{ fillOpacity: 0 }} // Use style with fillOpacity instead of opacity
                />
              }
              line={<Line strokeWidth={2} />}
              tooltip={
                <TooltipArea
                  tooltip={
                    <ChartTooltip
                      followCursor={true}
                      content={(tooltipData: any, color: string) => {
                        // Process data for tooltip
                        let date = tooltipData.x instanceof Date ? tooltipData.x : new Date();
                        const tooltipItems = [];

                        // Process data array if it exists
                        if (Array.isArray(tooltipData.data) && tooltipData.data.length > 0) {
                          // Iterate through all data array items
                          tooltipData.data.forEach((item: any) => {
                            if (item) {
                              // Get username and value
                              const userName = item.key || t('attendance.unknown');
                              const value = typeof item.value === 'number' ? item.value : 0;

                              // Find userId from metadata if available
                              const userId = item.metadata?.userId;

                              // Add to tooltip array
                              tooltipItems.push({
                                key: userName,
                                y: value,
                                color: item.color || color,
                                userId: userId, // Add userId for color lookup
                              });
                            }
                          });
                        }
                        // If no data array or it's empty, but items exist
                        else if (tooltipData.items && tooltipData.items.length > 0) {
                          // Get date from first element
                          date = tooltipData.items[0].key instanceof Date ? tooltipData.items[0].key : date;

                          // Iterate through items
                          tooltipData.items.forEach((item: any) => {
                            const seriesName = item.id || t('attendance.unknown');
                            const value = item.data || item.value || 0;

                            // Find userId from metadata if available
                            const userId = item.metadata?.userId;

                            // Add to tooltip array
                            tooltipItems.push({
                              key: seriesName,
                              y: value,
                              color: item.color || color,
                              userId: userId, // Add userId for color lookup
                            });
                          });
                        }

                        // If nothing found, check other structure variants
                        if (tooltipItems.length === 0 && tooltipData.key && tooltipData.data) {
                          // Case with single series element
                          const seriesName = tooltipData.key;

                          // Find userId from metadata if available
                          const userId = tooltipData.data.metadata?.userId;

                          tooltipItems.push({
                            key: seriesName,
                            y: tooltipData.data.data || tooltipData.data.value || 0,
                            color: color,
                            userId: userId, // Add userId for color lookup
                          });
                        }

                        // If still nothing found
                        if (tooltipItems.length === 0) {
                          tooltipItems.push({
                            key: t('common:states.noData'),
                            y: 0,
                            color: color,
                          });
                        }

                        // Form tooltip data
                        const finalTooltipData = {
                          x: date instanceof Date ? date.getTime() : date,
                          y: 0,
                          data: tooltipItems,
                          periodType, // Add periodType to tooltip data
                        };

                        return (
                          <CustomTooltipContentAttendanceChart
                            data={finalTooltipData}
                            color={color}
                            userColors={userColors} // Pass user colors array
                            periodType={periodType} // Pass period type to tooltip component
                          />
                        );
                      }}
                    />
                  }
                />
              }
            />
          }
          xAxis={
            <LinearXAxis
              type="time"
              tickSeries={
                <LinearXAxisTickSeries
                  label={<LinearXAxisTickLabel format={formatXAxisDate} fontSize={11} padding={5} />}
                />
              }
            />
          }
          yAxis={
            <LinearYAxis
              domain={[0, maxHoursValue]} // Dynamic range based on maximum value
              tickSeries={
                <LinearYAxisTickSeries
                  label={
                    <LinearYAxisTickLabel
                      format={(value: number) => t('attendance.hoursAxisLabel', { value })}
                      fontSize={11}
                      padding={5}
                    />
                  }
                />
              }
            />
          }
        />
      </div>

      {/* Right column with legend */}
      {apiData && apiData.users && Array.isArray(apiData.users) && onUserSelectionChange && (
        <UserAttendanceLegend
          apiData={apiData}
          userColors={userColors || []}
          selectedUsers={selectedUsers}
          onUserSelectionChange={onUserSelectionChange}
        />
      )}
    </div>
  );
};
